import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { ImageTypes } from '../enums/image-types';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ImageToSave } from '../images/models/image-to-save.model';
import { DocumentFileInfo } from '../images/models/document-file-info.model';
import { ImageForRecognition } from '../images/models/image-for-recognition.model';
import { VehicleImage } from '../purchase/models/vehicle-image.model';
import { PredictionResult } from '../images/models/prediction-result.model';
import { NotificationService } from './notification.service';
import { DomSanitizer } from '@angular/platform-browser';
import { UntypedFormGroup } from '@angular/forms';
import Compressor from 'compressorjs';
import { KeyValuePair } from '../models/key-value-pair.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalInputComponent } from '../modal-input/modal-input.component';
import { ImageUploadStatus } from '../enums/image-upload-status.enum';

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  constructor(private http: HttpClient,
    private notifications: NotificationService,
    private sanitizer: DomSanitizer,
    private modalService: NgbModal) { }

  imagesToSave: Array<ImageToSave> = new Array<ImageToSave>();
  imagesToDelete: Array<number> = new Array<number>();
  public imageAdded = new EventEmitter<boolean>();
  public allImagesAdded = new EventEmitter<boolean>();
  public saveDbImage = new EventEmitter<boolean>();
  pickerOpts = {
    types: [
      {
        description: "Images",
        accept: {
          "image/*": [".png", ".gif", ".jpeg", ".jpg"],
        },
      },
    ],
    excludeAcceptAllOption: true,
    multiple: false,
  };
  incPdfPickerOpts = {
    types: [
      {
        description: "Files",
        accept: {
          "image/*": [".png", ".gif", ".jpeg", ".jpg", ".pdf"],
        },
      },
    ],
    excludeAcceptAllOption: true,
    multiple: false,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getExistingImages$(quoteStateId: number): Observable<any> {
    const url = `${environment.ppxPrivateApi}api/image/getimages/${quoteStateId}`;
    return this.http
     // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get<any>(url)
      .pipe(catchError((err) => throwError(err)));
  }

  getExistingVehicleImages$(quoteStateId: number) {
    const url = `${environment.ppxPrivateApi}api/image/vehicle/${quoteStateId}`;
    return this.http
      .get<Array<VehicleImage>>(url)
      .pipe(catchError((err) => throwError(err)));
  }

  getImagePreview$(id: number, imageType) {
    const url = `${environment.ppxPrivateApi}api/image/getImagePreview/${id}/${imageType}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get<any>(url).pipe(catchError((err) => throwError(err)));
  }

  getImageRecognition$(image: ImageToSave, quoteStateId: number) {
    const imageObject = new ImageForRecognition();
    imageObject.fileName = image.fileName;
    imageObject.imgAsBase64 = image.imgAsBase64String.split(',')[1];
    imageObject.tag = image.tag;
    imageObject.threshold = image.threshold;
    const url = `${environment.ppxPrivateApi}api/Image/ImageRecognition/${quoteStateId}`;
    return this.http.post<PredictionResult>(url, imageObject,
      {
        headers: new HttpHeaders().set('Content-Type', 'application/json')
      }).pipe(
        catchError(err => throwError(err))
      );
  }

  deleteImages$(quoteStateId: number, images: Array<string>) {
    return this.http
      .post<string>(
        `${environment.ppxPrivateApi}api/image/vehicle/delete/${quoteStateId}`,
        images,
        {
          headers: new HttpHeaders().set('Content-Type', 'application/json'),
        }
      )
      .pipe(catchError(error => throwError(() => error)) );
  }



  submitImages$(
    quoteStateId: number,
    requiredImage: number,
    isPostPurchase: boolean
  ): Observable<Array<number>> {

    const formData = new FormData();
    for (const imageToDelete of this.imagesToDelete) {
      if (imageToDelete) {
        formData.append(`deleteIds`, imageToDelete.toString());
      }
    }

    // filter for duplicates
    this.imagesToSave = this.imagesToSave.filter(function (item, pos, self) {
      return self.indexOf(item) === pos;
    });

    for (let index = 0; index < this.imagesToSave.length; index++) {
      if (this.imagesToSave[index].image && !this.imagesToSave[index].isSaved) {
        let fileName: string;
        if (this.imagesToSave[index].fileName) {
          fileName = this.imagesToSave[index].fileName;
        } else {
          fileName = `${this.imagesToSave[index].stampCode}-${this.imagesToSave[index].parameterName
            }-${index + 1}${this.imagesToSave[index].extension}`;
        }

        formData.append(
          `image${index}`,
          this.imagesToSave[index].image,
          fileName
        );
        formData.append(
          'fileInfo',
          JSON.stringify(
            new DocumentFileInfo(
              this.imagesToSave[index].parameterName ??
              this.imagesToSave[index].documentName,
              this.imagesToSave[index].stampCode,
              fileName,
              this.imagesToSave[index].tag,
              this.imagesToSave[index].threshold,
              this.imagesToSave[index].recognitionResult
            )
          )
        );
      }
    }
    let url = '';
    if (requiredImage) {
      if (requiredImage === ImageTypes.VehiclePhoto) {
        url = `${environment.ppxPrivateApi}api/image/vehicle/${quoteStateId}/${isPostPurchase}`;
      } else {
        url = `${environment.ppxPrivateApi}api/image/save/${quoteStateId}/${isPostPurchase}`;
      }
    } else {
      url = `${environment.ppxPrivateApi}api/image/save/${quoteStateId}/${isPostPurchase}`;
    }
    return this.http
      .post<Array<number>>(url, formData);
  }

  addImagesToSave(imagesToSave) {
    this.imagesToSave.push(...imagesToSave);
  }

  removeImagesToSave() {
    this.imagesToSave = new Array<ImageToSave>();
  }

  addImagesToDelete(imagesToDelete) {
    this.imagesToDelete.push(...imagesToDelete);
  }

  removeImagesToDelete() {
    this.imagesToDelete = new Array<number>();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  submitVideoChunk$(quoteStateId: number, chunk: any, index: number): Observable<void> {
    const formData = new FormData();
    formData.append('video', chunk);
    return this.http
      .post<void>(`${environment.ppxPrivateApi}api/image/video/${quoteStateId}/${index}`, formData)
      .pipe(catchError((err) => throwError(err)));
  }

  completeVideoUpload$(quoteStateId: number, isEngineVideo: string): Observable<void> {
    return this.http
      .post<void>(`${environment.ppxPrivateApi}api/image/video/complete/${quoteStateId}/${isEngineVideo}`, null)
      .pipe(catchError((err) => throwError(err)));
  }

  recordVideoSubmissionFailure$(quoteStateId: number, videoType: string): Observable<boolean> {
    return this.http
      .post<boolean>(`${environment.ppxPrivateApi}api/image/video/attempt/${quoteStateId}/${videoType}`, null)
      .pipe(catchError((err) => throwError(err)));
  }

  getVideo$(quoteStateId: number, videoType: string): Observable<VehicleImage> {
    const url = `${environment.ppxPrivateApi}api/image/video/${quoteStateId}/${videoType}`;
    return this.http.get<VehicleImage>(url).pipe(catchError((err) => throwError(err)));
  }

  cancelVideoUpload$(quoteStateId: number): Observable<void> {
    const url = `${environment.ppxPrivateApi}api/image/video/cancel/${quoteStateId}`;
    return this.http.get<void>(url).pipe(
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  getVideoFileName$(quoteStateId: number, videoType: string): Observable<string> {
    const url = `${environment.ppxPrivateApi}api/image/videoFileName/${quoteStateId}/${videoType}`;
    return this.http.get<{ fileName: string }>(url).pipe(map(response => response.fileName), catchError((err) => throwError(err)));
  }

  getVideoUrl(fileName: string): Observable<Blob> {
    const url = `${environment.ppxPrivateApi}api/image/stream/${fileName}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  isValidBlob(blob: Blob): boolean {
    return blob instanceof Blob && blob.size > 0;
  }

  checkBlobUrl(blobUrl): Promise<boolean> {
    return fetch(blobUrl)
      .then(response => {
        return response.ok
      })
      .catch(() => {
        return false;
      });
  }

  isBlobURL(url) {
    return url.startsWith("blob:");
  }

  readFileAsync(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => {
        const error = reader.error;
        reject(new Error(`Failed to read file ${reader.error}:  ${error.name} - ${error.message}`));
      }
      reader.readAsDataURL(file);
    });
  }

  checkUniqueName(fileName: string): boolean {
    const existing = this.imagesToSave.find(function (element) {
      return element.image?.fileName === fileName;
    });
    if (existing) {
      this.notifications.warningToast(
        'An image with this name has already been added'
      );
    }
    return !existing;
  }

  createUrl(photo) {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(photo));
  }

  getFormControl(parameterName: string, questionForm: UntypedFormGroup): UntypedFormGroup {
    if (questionForm) {
      return questionForm.get('items')['controls'].filter(x => x.value.name === parameterName)[0];
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reduceFileSize(file, quality, loggingService, quoteStateId, selectedImage): Promise<any> {
    const logService = loggingService;
    const id = quoteStateId;
    const stamp = selectedImage.documentName;
    const originalFile = structuredClone(file);
    return new Promise((resolve) => {
      new Compressor(file, {
        quality: quality,
        success(result) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          resolve(result as any);
        },
        error(err) {
          const error = {
            message: `Failed to compress ${stamp} image  for quoteStateId ${id} - fileSize: ${(originalFile && typeof originalFile.size === 'number') ? originalFile.size : undefined}`,
            stack: err.stack
          };
          logService.logError(error);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          resolve(originalFile as any);
        },
      });
    });
  }

  saveImage$(quoteStateId, requiredImage, isPostPurchase, imgToSave) {
    const url = `${environment.ppxPrivateApi}api/image/saveImage/${quoteStateId}/${requiredImage}/${isPostPurchase}`;
    const formData = new FormData();
    let fileName: string;
    if (imgToSave.fileName) {
      fileName = imgToSave.fileName;
    } else {
      fileName = `${imgToSave.stampCode}-${imgToSave.parameterName
        }-${imgToSave.extension}`;
    }

    formData.append(
      `image`,
      imgToSave.image,
      fileName
    );

    formData.append(
      'fileInfo',
      JSON.stringify(
        new DocumentFileInfo(
          imgToSave.parameterName ??
          imgToSave.documentName,
          imgToSave.stampCode,
          fileName,
          imgToSave.tag,
          imgToSave.threshold,
          imgToSave.recognitionResult
        )
      )
    );
    return this.http
      .post<{ success: boolean; id: number }>(url, formData).pipe(catchError((err) => { return throwError(() => new Error(err))}));
  }

  deleteImage$(documentScanId, requiredImage) {
    return this.http
      .post<string>(
        `${environment.ppxPrivateApi}api/image/deleteImage/${documentScanId}/${requiredImage}`, null,
        {
          headers: new HttpHeaders().set('Content-Type', 'application/json'),
        }
      )
      .pipe(catchError((err) => throwError(err)));
  }

  getFileName(imageToSave: ImageToSave): string {
    const timestamp = new Date().getTime();
    return imageToSave.stampCode.toString() + '-' + imageToSave.parameterName + '-' + timestamp.toString() + imageToSave.extension;
  }

  checkForMismatch(images) {
    let mismatch = false;
    let hasMismatch = false;
    images.forEach(element => {
      if (element.hasMismatch) {
        mismatch = true;
      }
    });
    if (mismatch) {
      hasMismatch = true;
    } else {
      hasMismatch = false;
    }

    return hasMismatch;
  }

  checkAuthorization(authCheck, hasMismatch) {
    if (authCheck) {
      if (hasMismatch) {
        authCheck.controls.value.setValue('true');
      } else {
        authCheck.controls.value.setValue('false');
      }
    }
  }

  showModal(images, requiredImageType, isPostPurchase) {
    const options = new Array<KeyValuePair>();
    let index = 0;
    const image = new ImageToSave();
    images.forEach(img => {
      if (!options.find(x => x.value === img.documentName)) {
        options.push(new KeyValuePair(index.toString(), img.documentName));
      }
      index += 1;
    });
    if (requiredImageType === ImageTypes.ScannedDocument && !options.find(x => x.value === 'Other')) {
      options.push(new KeyValuePair('-1', 'Other'));
    }
    if (requiredImageType === ImageTypes.ScannedDocument && !options.find(x => x.value === 'Keys')) {
      options.push(new KeyValuePair('-2', 'Keys'));
    }
    if (requiredImageType === ImageTypes.ScannedDocument && !options.find(x => x.value === 'Dashboard')) {
      options.push(new KeyValuePair('-3', 'Dashboard'));
    }

    const modalRef = this.modalService.open(ModalInputComponent, {
      keyboard: false,
      centered: true,
      size: 'lg',
    });
    modalRef.componentInstance.title =
      requiredImageType === ImageTypes.ScannedDocument
        ? 'Please select additional documents:'
        : 'Please select additional vehicle images:';
    modalRef.componentInstance.close = true;
    modalRef.componentInstance.options = options;

    modalRef.result.then(
      (result) => {
        const resIndex = parseInt(result, 10);

        if (resIndex === -1) {
          image.documentName = 'Other';
          image.stampCode = 'Unknown';
          image.parameterName = 'Other';
        } else if (resIndex === -2) {
          image.documentName = 'Keys';
          image.stampCode = 'K';
          image.parameterName = 'Keys';
        } else if (resIndex === -3) {
          image.documentName = 'Dashboard';
          image.stampCode = 'DB';
          image.parameterName = 'Dashboard';
        } else {
          image.documentName = images[resIndex].documentName;
          image.stampCode = images[resIndex].stampCode;
          image.parameterName = images[resIndex].parameterName;
          image.allowPdf = images[resIndex].allowPdf;
        }
        images.push(image);
        this.allImagesAdded.emit(this.checkAllImagesAdded(images, requiredImageType, isPostPurchase));
      },
      () => { }
    );
  }

  checkAllImagesAdded(images, imageType, isPostPurchase) {
    if (imageType === ImageTypes.VehiclePhoto) {
      return true;
    } else if (isPostPurchase) {
      return images.some((i) => i.imgAsBase64String && i.documentScanId > 0);
    } else {
      return images.every((i) => i.imgAsBase64String && i.uploadStatus != ImageUploadStatus.Failed);
    }
  }
}
